import { useEffect, useState } from "react";

import {
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import Icon from "@mui/material/Icon";

import { DrawerForm, DrawerFormAddButton, DrawerFormRow, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField, IconAutocomplete } from "../../../../components/forms";
import { CONTACT_TYPE_OPTIONS, ENTITY_STATUS_OPTIONS } from "../../../../lib/ui/iconSelectOptions";
import { DRAWER_FORM_GRID_SPACING, drawerReadonlyFieldSx } from "../../../../lib/ui/dialogFormLayout";
import { useTranslation } from "react-i18next";

type ContactType =
  | "PHONE"
  | "MOBILE"
  | "EMAIL"
  | "OTHER"
  | "SIEM"
  | "BACKUP"
  | "FIREWALL"
  | "EPP"
  | "XDR"
  | "DLP"
  | "ISP"
  | "FILE_AUDIT"
  | "MONITORING_TOOL";

type Contact = {
  name: string;
  type: ContactType;
  value: string;
};

type Source = {
  name: string;
  type: ContactType;
  value: string;
};

const toLogoBase64 = (logo?: string | null) => {
  if (!logo) {
    return "";
  }
  return logo.includes(",") ? logo.split(",")[1] || "" : logo;
};

const ProfileSourceSystemDialogBody = (item: unknown) => {
  const { t } = useTranslation();
  const dialogItem = (item ?? {}) as Record<string, unknown>;
  const isEdit = typeof dialogItem.id === "string" && dialogItem.id.length > 0;

  const [name, setName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>("ACTIVE");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sources, setSources] = useState<Source[]>([]);

  useEffect(() => {
    if (!isEdit) {
      setName("");
      setSelectedStatus("ACTIVE");
      setContacts([]);
      setSources([]);
      return;
    }

    setName(typeof dialogItem.name === "string" ? dialogItem.name : "");
    setSelectedStatus(typeof dialogItem.status === "string" ? dialogItem.status : "ACTIVE");
    setContacts(Array.isArray(dialogItem.contacts) ? (dialogItem.contacts as Contact[]) : []);
    setSources(Array.isArray(dialogItem.sources) ? (dialogItem.sources as Source[]) : []);
  }, [dialogItem.id, dialogItem.name, dialogItem.status, dialogItem.contacts, dialogItem.sources, isEdit]);

  const addContact = () => {
    setContacts((prev) => [...prev, { name: "", type: "EMAIL", value: "" }]);
  };

  const removeContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateContact = (index: number, field: keyof Contact, value: string) => {
    setContacts((prev) =>
      prev.map((contact, i) => (i === index ? { ...contact, [field]: value } : contact)),
    );
  };

  const addSource = () => {
    setSources((prev) => [...prev, { name: "", type: "EMAIL", value: "" }]);
  };

  const removeSource = (index: number) => {
    setSources((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSource = (index: number, field: keyof Source, value: string) => {
    setSources((prev) =>
      prev.map((source, i) => (i === index ? { ...source, [field]: value } : source)),
    );
  };

  const logoValue = isEdit
    ? toLogoBase64(typeof dialogItem.logo === "string" ? dialogItem.logo : "")
    : "";

  return (
    <DrawerForm>
      <DrawerFormSection title={t("form.identity")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          {isEdit ? (
            <Grid size={12}>
              <TextField
                fullWidth
                id="id"
                name="id"
                label={t("common.id")}
                variant="outlined"
                size="small"
                value={String(dialogItem.id || "")}
                slotProps={{ input: { readOnly: true } }}
                sx={drawerReadonlyFieldSx}
              />
            </Grid>
          ) : null}
          <Grid size={12}>
            <TextField
              autoFocus={!isEdit}
              required
              fullWidth
              id="sourceName"
              name="sourceName"
              label={t("common.name")}
              variant="outlined"
              size="small"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("form.sourceSystemName")}
            />
          </Grid>
          <Grid size={12}>
            <IconAutocomplete
              options={ENTITY_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={setSelectedStatus}
              label={t("common.status")}
              required
              noOptionsText="No status options found"
            />
            <FormHiddenField name="sourceStatus" value={selectedStatus || ""} />
            <FormHiddenField name="sourceLogo" value={logoValue} />
            {typeof dialogItem.customerId === "string" && (
              <FormHiddenField name="customerId" value={dialogItem.customerId} />
            )}
          </Grid>
        </Grid>
      </DrawerFormSection>

      <DrawerFormSection title={t("form.contacts")} action={<DrawerFormAddButton onClick={addContact} label={t("common.add")} />}>
        {contacts.map((contact, index) => (
          <DrawerFormRow key={index}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.name")}
              value={contact.name}
              onChange={(event) => updateContact(index, "name", event.target.value)}
            />
            <IconAutocomplete
              options={CONTACT_TYPE_OPTIONS}
              value={contact.type}
              onChange={(value) => updateContact(index, "type", value || "EMAIL")}
              noOptionsText="No type options found"
              sx={{ minWidth: 132, maxWidth: 148 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.value")}
              value={contact.value}
              onChange={(event) => updateContact(index, "value", event.target.value)}
            />
            <IconButton size="small" onClick={() => removeContact(index)} color="error">
              <Icon>remove</Icon>
            </IconButton>
          </DrawerFormRow>
        ))}
        <FormHiddenField name="contacts" value={JSON.stringify(contacts)} />
      </DrawerFormSection>

      <DrawerFormSection title={t("form.sources")} action={<DrawerFormAddButton onClick={addSource} label={t("common.add")} />}>
        {sources.map((source, index) => (
          <DrawerFormRow key={index}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.name")}
              value={source.name}
              onChange={(event) => updateSource(index, "name", event.target.value)}
            />
            <IconAutocomplete
              options={CONTACT_TYPE_OPTIONS}
              value={source.type}
              onChange={(value) => updateSource(index, "type", value || "EMAIL")}
              placeholder={t("common.type")}
              noOptionsText="No type options found"
              sx={{ minWidth: 132, maxWidth: 148 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.value")}
              value={source.value}
              onChange={(event) => updateSource(index, "value", event.target.value)}
            />
            <IconButton size="small" onClick={() => removeSource(index)} color="error">
              <Icon>remove</Icon>
            </IconButton>
          </DrawerFormRow>
        ))}
        <FormHiddenField name="sources" value={JSON.stringify(sources)} />
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default ProfileSourceSystemDialogBody;
